import {
  Button,
  Card,
  Col,
  Divider,
  Form,
  Input,
  Row,
  Space,
  Switch,
  Tooltip,
} from "antd";
import GenericTable from "../../../components/Commons/Table/generic-table.component";
import { CopyOutlined, DeleteOutlined, EditOutlined } from "@ant-design/icons";
import ShowDataLength from "../../../components/Commons/Table/results-length-table.component";
import { useState } from "react";
import { getAll, remove, update } from "../../../services/plans.service";
import { PaginationProps } from "antd/lib";
import { FilterValue } from "antd/es/table/interface";
import { useNavigate } from "react-router-dom";
import { AlertService } from "../../../services/alert.service";
import { useMutation, useQuery } from "react-query";
import ConfirmModal from "../../../components/Commons/Modal/confirm-modal.component";
import ProfilePermissionService from "../../../services/profilePermissions.service";
import { frequencies } from "../../../Enums/Frequency.enums";
import { formatCurrency } from "../../../services/utils";
import { isMobile } from "react-device-detect";

const { Search } = Input;

export default function List() {
  const [plans, setPlans] = useState([]);
  const [currentPage, setCurrentPage] = useState<number | undefined>(1);
  const [perPage, setPerPage] = useState<number | undefined>(10);
  const [total, setTotal] = useState<number>(0);
  const [search, setSearch] = useState<string>("");
  const [order, setOrder] = useState<string>("name-asc");
  const [removePlan, setRemovePlan] = useState<any | undefined>(undefined);
  const [formSearch] = Form.useForm();

  const navigate = useNavigate();

  const profilePermissionService = ProfilePermissionService.getInstance();

  const { isLoading: planLoading, refetch: loadPlans } = useQuery(
    ["loadPlans", currentPage, search, order, perPage],
    () => getAll({ page: currentPage, per_page: perPage, order, search }),
    {
      onSuccess: (response) => {
        setPlans(
          response.data.map((plan: any) => {
            return {
              key: plan.data.id,
              ...plan.data,
            };
          })
        );

        setCurrentPage(response.meta.current_page);
        setPerPage(response.meta.per_page);
        setTotal(response.meta.total);
      },
      refetchOnWindowFocus: false,
    }
  );

  const { mutate: removePlanMutate } = useMutation(remove, {
    onSuccess: () => {
      AlertService.sendAlert([{ text: "Plano apagada com sucesso." }]);
      setRemovePlan(undefined);
      loadPlans();
    },
    onError: () => setRemovePlan(undefined),
  });

  const onSearch = (search_value: any) => {
    setCurrentPage(1);
    setSearch(search_value);
  };

  const handleTableChange = (
    pagination: PaginationProps,
    filters: Record<string, FilterValue | null>,
    sorter: any
  ) => {
    if (pagination.current != currentPage) {
      setCurrentPage(pagination.current);
    }

    if (pagination.pageSize != perPage) {
      setPerPage(pagination.pageSize);
      setCurrentPage(1);
    }

    if (sorter.order) {
      let orderBy = `${sorter.field}-${sorter.order.substr(
        0,
        sorter.order.length - 3
      )}`;

      if (order != orderBy) {
        setOrder(orderBy);
      }
    }
  };

  const toPage = (page: string, plan_id: number = 0) => {
    let pages = {
      create: "/management/plans/create",
      edit: `/management/plans/${plan_id}/edit`,
      duplicate: `/management/plans/${plan_id}/duplicate`,
    };

    navigate(pages[page as keyof typeof pages]);
  };

  const { mutate: updateActiveStatusMutate } = useMutation(update, {
    onSuccess: () => {
      AlertService.sendAlert([
        { text: "Status do plano atualizado com sucesso." },
      ]);
    },
  });

  const onChangeActiveStatus = (checked: boolean, uuid: string) => {
    const obj = {
      values: { active: checked },
      uuid,
    };

    updateActiveStatusMutate(obj);
  };

  const columns: any = [
    { title: "Plano", dataIndex: "name", sorter: true, key: "name" },
    {
      title: "Frequência",
      dataIndex: "frequency",
      sorter: true,
      key: "frequency",
      render: (_: any, record: any) =>
        frequencies.find((frequency) => frequency.value === record.frequency)
          ?.label || "",
      responsive: ["md", "lg", "xl"],
    },
    {
      title: "Preço",
      dataIndex: "price",
      sorter: true,
      key: "price",
      render: (_: any, record: any) => formatCurrency(record.price || 0),
      responsive: ["md", "lg", "xl"],
    },
    {
      title: "Desconto",
      dataIndex: "discount",
      sorter: true,
      key: "discount",
      render: (_: any, record: any) => formatCurrency(record.discount || 0),
      responsive: ["md", "lg", "xl"],
    },
    {
      title: "Ativo?",
      dataIndex: "active",
      key: "active",
      sorter: true,
      width: 150,
      render: (_: any, record: any) => {
        return (
          <Tooltip title={record.active ? "Desativar plano" : "Ativar plano"}>
            <Switch
              checked={record.active}
              onChange={(event: boolean) => {
                onChangeActiveStatus(event, record.uuid);
                record.active = event;
              }}
            />
          </Tooltip>
        );
      },
      responsive: ["md", "lg", "xl"],
    },
    {
      title: "Ações",
      key: "action",
      render: (_: any, record: any) =>
        profilePermissionService.hasPermission("plans-plans:write") && (
          <Space size="small" direction={isMobile ? "vertical" : "horizontal"}>
            <Tooltip placement="top" title="Duplicar">
              <Button
                type="default"
                shape="circle"
                size="middle"
                icon={<CopyOutlined />}
                onClick={() => toPage("duplicate", record.uuid)}
              />
            </Tooltip>

            <Tooltip placement="top" title="Editar">
              <Button
                type="default"
                shape="circle"
                size="middle"
                icon={<EditOutlined />}
                onClick={() => toPage("edit", record.uuid)}
              />
            </Tooltip>

            <Tooltip placement="top" title="Apagar">
              <Button
                type="default"
                shape="circle"
                size="middle"
                icon={<DeleteOutlined />}
                onClick={() => setRemovePlan(record)}
              />
            </Tooltip>
          </Space>
        ),
      width: "15%",
    },
  ];

  return (
    <>
      <Card
        styles={{
          body: { padding: 8 },
        }}
      >
        <Row>
          <Col
            lg={{ span: 12 }}
            md={{ span: 12 }}
            sm={{ span: 24 }}
            xs={{ span: 24 }}
          >
            <Form
              form={formSearch}
              name="search_plans"
              layout="vertical"
              autoComplete="off"
            >
              <Form.Item name="search_plan">
                <Search
                  onSearch={onSearch}
                  placeholder="Pesquisa a plano"
                  enterButton="Pesquisar"
                  allowClear
                  size="large"
                  maxLength={255}
                />
              </Form.Item>
            </Form>
          </Col>

          {profilePermissionService.hasPermission("plans-plans:write") && (
            <Col
              lg={{ span: 4, offset: 8 }}
              md={{ span: 4, offset: 8 }}
              sm={{ span: 24 }}
              xs={{ span: 24 }}
            >
              <Button
                type="primary"
                onClick={() => toPage("create")}
                size="large"
                block
              >
                Criar plano
              </Button>
            </Col>
          )}
        </Row>

        <Divider />

        <div className="plan">
          <GenericTable
            columns={columns}
            dataSource={plans}
            pagination={{
              current: currentPage,
              pageSize: perPage,
              showSizeChanger: true,
              total: total,
            }}
            loading={planLoading}
            totalChildren={
              <div className="zone_result_size_n_export">
                <ShowDataLength size={total} className="show_result_size" />
              </div>
            }
            empty={{
              createTheFirst: !plans.length && !search,
              textOne:
                "Este plano encontra-se vazia, uma vez que ainda não foram criadas planos.",
              textTwo: profilePermissionService.hasPermission(
                "plans-plans:write"
              )
                ? `Para começar, crie um novo plano.`
                : "",
              link: profilePermissionService.hasPermission("plans-plans:write")
                ? `/management/plans/create`
                : "",
              createText: "Criar plano",
            }}
            handleTableChange={handleTableChange}
          />

          <ConfirmModal
            open={!!removePlan}
            title={`Tem certeza que deseja apagar a plano "${removePlan?.name}"?`}
            okText="Apagar"
            cancelText="Voltar"
            onOk={() => removePlanMutate(removePlan?.id)}
            onCancel={() => setRemovePlan(undefined)}
          ></ConfirmModal>
        </div>
      </Card>
    </>
  );
}
